import { stack, stackOffsetExpand } from 'd3-shape';
import { chartSeries, type QdsChartSeries } from '../chart/chart-colors.js';

export interface QdsTimelinePeriod {
  id: string;
  start: string;
  end: string;
  label?: string;
  state?: 'complete' | 'missing';
  values: Record<string, number>;
  /** Exact display strings supplied by the application; geometry always uses numeric values. */
  displayValues?: Record<string, string>;
}
export interface QdsTimelineRange { start: string; end: string; }
export interface TimelineInterval extends QdsTimelinePeriod {
  startTime: number; endTime: number; missing: boolean;
  segments: { seriesId: string; value: number; from: number; to: number }[];
}

/** UTC calendar dates only, avoiding browser locale, timezone, and DST shifts. End dates are exclusive. */
export function timelineDate(value: string): number {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Timeline dates must use YYYY-MM-DD.');
  const time = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(time) || new Date(time).toISOString().slice(0, 10) !== value) throw new Error('Timeline dates must be real UTC calendar dates.');
  return time;
}
export function timelineDateText(time: number): string { return new Date(time).toISOString().slice(0, 10); }

export function layoutTimeline(inputSeries: readonly QdsChartSeries[], inputPeriods: readonly QdsTimelinePeriod[]) {
  const series = chartSeries(inputSeries);
  if (!Array.isArray(inputPeriods)) throw new Error('Periods must be an array.');
  if (inputPeriods.length && !series.length) throw new Error('Provide series before timeline periods.');
  const seriesIds = new Set(series.map(item => item.id)), ids = new Set<string>();
  const periods: TimelineInterval[] = inputPeriods.map(period => {
    if (!period || typeof period.id !== 'string' || !period.id || ids.has(period.id)) throw new Error('Periods need unique, nonempty IDs.');
    ids.add(period.id);
    const startTime = timelineDate(period.start), endTime = timelineDate(period.end);
    if (endTime <= startTime) throw new Error('Each period must end after it starts.');
    if (period.state !== undefined && !['complete', 'missing'].includes(period.state)) throw new Error('Unknown period state.');
    if (!period.values || typeof period.values !== 'object' || Array.isArray(period.values)) throw new Error('Period values must be a series/value object.');
    for (const [id, value] of Object.entries(period.values)) {
      if (!seriesIds.has(id)) throw new Error('A period references an unknown series.');
      if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new Error('Timeline values must be finite, nonnegative numbers.');
    }
    const values: Record<string, number> = { ...period.values };
    const total = Object.values(values).reduce((sum, value) => sum + value, 0);
    if (!Number.isFinite(total)) throw new Error('Period total exceeds the supported chart range.');
    return { ...period, values, displayValues: { ...period.displayValues }, startTime, endTime,
      missing: period.state === 'missing' || total === 0, segments: [] };
  }).sort((a, b) => a.startTime - b.startTime);
  for (let i = 1; i < periods.length; i++) if (periods[i].startTime < periods[i - 1].endTime) throw new Error('Timeline periods must not overlap.');
  const layers = stack<TimelineInterval>().keys(series.map(item => item.id))
    .value((period, id) => period.missing ? 0 : period.values[id] || 0).offset(stackOffsetExpand)(periods);
  layers.forEach(layer => layer.forEach((point, index) => {
    if (!periods[index].missing) periods[index].segments.push({ seriesId: layer.key, value: periods[index].values[layer.key] || 0, from: point[0], to: point[1] });
  }));
  return { series, periods };
}
